import { dehydrate, QueryClient } from '@tanstack/react-query';
import { Product as ProductComponent } from 'components/pages/Product';
import {
  fetchProductEnabledCountries,
  PRODUCT_ENABLED_COUNTRIES_KEY,
} from 'hooks/localisation/useProductEnabledCountries';
import {
  fetchProductPage,
  PRODUCT_PAGE_KEY,
  useProductPage,
} from 'hooks/pages/useProductPage';
import { fetchSanityProductSlugs } from 'hooks/product/useSanityProductSlugs';
import type { ShopifyProductQueryKey } from 'hooks/product/useShopifyProduct';
import {
  fetchShopifyProduct,
  SHOPIFY_PRODUCT_QUERY,
  useShopifyProduct,
} from 'hooks/product/useShopifyProduct';
import {
  fetchProductReviews,
  PRODUCT_REVIEWS_KEY,
} from 'hooks/reviews/useProductReviews';
import {
  fetchReviewedProducts,
  REVIEWED_PRODUCTS_KEY,
} from 'hooks/reviews/useReviewedProducts';
import { useAnalytics } from 'hooks/useAnalytics';
import { GLOBALS_KEY } from 'hooks/useGlobals';
import { env } from 'lib/constants/env';
import { PRODUCTS, SKINCARE_PRODUCTS } from 'lib/constants/routes';
import { DEFAULT_COUNTRY_CODE } from 'lib/constants/siteDefaults';
import { useRouter } from 'next/router';
import { useEffectOnce } from 'react-use';
import type { Globals } from 'types/cms/Globals';
import type { PageContext } from 'types/common/PageContext';
import type { SiteCategory } from 'types/common/SiteCategory';
import { blocksBuildTimeResolver, prefetchCommonData } from 'utils/buildTime';
import { fetchWithSentry } from 'utils/pageTemplates/fetchWithSentry';
import { getReviewProductSlugs } from 'utils/reviews/getReviewProductSlugs';
import { reviewedProductsSelector } from 'utils/reviews/reviewedProductsSelector';

interface ProductPageOptions {
  category: SiteCategory;
}

const PRODUCT_PATHS: Record<SiteCategory, string> = {
  supplements: PRODUCTS,
  skincare: SKINCARE_PRODUCTS,
};

export function productPage({ category }: ProductPageOptions) {
  async function getStaticProps(context: PageContext) {
    const queryClient = new QueryClient();

    await prefetchCommonData(queryClient);

    const page = await fetchWithSentry({
      queryClient,
      queryKey: [PRODUCT_PAGE_KEY, context.params.slug],
      queryFn: fetchProductPage,
      tags: {
        template: 'productPage',
        slug: context.params.slug,
        api: 'sanity',
      },
    });

    if (!page) {
      return {
        notFound: true,
        revalidate: env.ISR_TTL_SECONDS,
      };
    }

    /* INFO: Redirecting here rather than in next.config: config redirects aren't
     * applied when Next fetches page data during client-side navigation, so a
     * NextLink to the old URL would render a blank page. */
    if (page.category !== category) {
      return {
        redirect: {
          destination: `${PRODUCT_PATHS[page.category]}/${context.params.slug}`,
          permanent: true,
        },
      };
    }

    await blocksBuildTimeResolver(queryClient, page.blocks);

    const reviewedProducts = await fetchWithSentry({
      queryClient,
      queryKey: [REVIEWED_PRODUCTS_KEY],
      queryFn: fetchReviewedProducts,
      tags: {
        template: 'productPage',
        slug: context.params.slug,
        api: 'sanity',
      },
    });

    const shopifyProduct = await fetchWithSentry({
      queryClient,
      queryKey: [
        SHOPIFY_PRODUCT_QUERY,
        context.params.slug,
        DEFAULT_COUNTRY_CODE,
      ] satisfies ShopifyProductQueryKey,
      queryFn: fetchShopifyProduct,
      tags: {
        template: 'productPage',
        slug: context.params.slug,
        api: 'shopify',
      },
    });

    // Prefetch sibling format Shopify products so ProductSeo can compose the
    // ProductGroup JSON-LD synchronously on render. Per-sibling failures are
    // tolerated; a missing sibling is omitted from the family rather than
    // breaking the parent page.
    const siblingSlugs = (page.productFormats ?? [])
      .map((format) => format.product.slug)
      .filter((slug) => slug !== context.params.slug);
    await Promise.all(
      siblingSlugs.map((slug) =>
        fetchWithSentry({
          queryClient,
          queryKey: [
            SHOPIFY_PRODUCT_QUERY,
            slug,
            DEFAULT_COUNTRY_CODE,
          ] satisfies ShopifyProductQueryKey,
          queryFn: fetchShopifyProduct,
          tags: {
            template: 'productPage',
            slug: context.params.slug,
            api: 'shopify',
            sibling: slug,
          },
        }).catch(() => undefined),
      ),
    );

    const globals = queryClient.getQueryData([GLOBALS_KEY]) as Globals;

    if (globals) {
      const slugsToFilter = getReviewProductSlugs(
        page.bundleProducts,
        context.params.slug,
      );

      const reviewedProductsForPage = reviewedProductsSelector({
        globals,
        reviewedProducts,
        slugsToFilter,
      });

      const productReviewIds = reviewedProductsForPage
        ?.filter((item) => (item.ratingCount ?? 0) > 0)
        .map((item) => item.id.toString());

      if (productReviewIds?.length) {
        await queryClient.prefetchQuery({
          queryKey: [PRODUCT_REVIEWS_KEY, productReviewIds, null, false],
          queryFn: fetchProductReviews,
        });
      }

      if (shopifyProduct) {
        const countryCodes = globals.availableCountries.map(
          (availableCountry) => availableCountry.isoCode,
        );

        await queryClient.prefetchQuery({
          queryKey: [
            PRODUCT_ENABLED_COUNTRIES_KEY,
            shopifyProduct.id,
            countryCodes,
          ],
          queryFn: fetchProductEnabledCountries,
        });
      }
    }

    return {
      revalidate: env.ISR_TTL_SECONDS,
      props: {
        key: context.params.slug,
        slug: context.params.slug,
        dehydratedState: dehydrate(queryClient),
      },
    };
  }

  async function getStaticPaths() {
    const products = await fetchSanityProductSlugs(category);

    const paths = products.map((product) => ({
      params: { slug: product.slug },
    }));

    return { paths, fallback: 'blocking' };
  }

  interface ProductPageProps {
    slug: string;
  }

  function ProductPageComponent({ slug }: ProductPageProps) {
    const router = useRouter();
    const { analytics } = useAnalytics();
    const { data: shopifyProduct } = useShopifyProduct(slug);

    const { data: page } = useProductPage(slug);
    const { mostPopularVariantId } = page;

    useEffectOnce(() => {
      if (!shopifyProduct) return;

      analytics.viewProductPage({
        product: shopifyProduct,
        pagePath: router.asPath,
      });

      analytics.viewItem({
        product: shopifyProduct,
        merchandiseId: mostPopularVariantId || shopifyProduct.variants[0]?.id,
      });
    });

    return <ProductComponent slug={slug} />;
  }

  return {
    getStaticProps,
    getStaticPaths,
    ProductPageComponent,
  };
}
